// The per-row "copy as structured text" payload (spec-4 dec-1, ac-3/ac-9). The
// outreach task is per-user, so the egress is per-user: this serialises exactly
// ONE user's record to the clipboard as human-readable markdown plus a machine-
// parseable JSON block, so a colleague's screen-automation agent (Claude Cowork)
// can ingest a clean payload and draft a bespoke message. It is a pure function
// over already-rendered, already-authorised data — no server round-trip, no bulk.
import type { UserRow } from "../api/types";

function fmtDate(iso: string): string {
  // YYYY-MM-DD — stable, locale-independent.
  return iso.slice(0, 10);
}

/** Build the clipboard text for a single user. Pure — caller writes it to the
 *  clipboard. Never accepts more than one user (no bulk export path exists). */
export function userToClipboardText(u: UserRow): string {
  const orgs = u.orgs.length
    ? u.orgs.map((o) => `${o.orgName} (${o.role})`).join(", ")
    : "—";
  const tags = u.tags.length ? u.tags.map((t) => `${t.scope}:${t.value}`).join(", ") : "—";
  const json = JSON.stringify(
    {
      name: u.name,
      email: u.email,
      orgs: u.orgs,
      signedUpAt: u.createdAt,
      specCount: u.specCount,
      decisionCount: u.decisionCount,
      acCount: u.acCount,
      mcpConnected: u.mcpConnected,
      tags: u.tags.map((t) => `${t.scope}:${t.value}`),
    },
    null,
    2,
  );
  return [
    `# ${u.name ?? "Unknown"} (${u.email})`,
    `- Org(s): ${orgs}`,
    `- Signed up: ${fmtDate(u.createdAt)}`,
    `- Specs authored: ${u.specCount}`,
    `- Decisions: ${u.decisionCount}`,
    `- Acceptance criteria: ${u.acCount}`,
    `- MCP connected: ${u.mcpConnected ? "yes" : "no"}`,
    `- Tags: ${tags}`,
    "",
    "```json",
    json,
    "```",
    "",
  ].join("\n");
}
